/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { rpMediaQueries } from '../../core/theme'
import { useRussianPokerStore } from '../../providers/RussianPokerProvider'
import { WalletIcon } from '../Icons'
import { Typography } from '../Typography'

const createBlinkAnimation = (baseColor: string, highlightColor: string) =>
  keyframes`
    0%,
    100% {
      color: ${baseColor};
      opacity: 0.9;
    }

    50% {
      color: ${highlightColor};
      opacity: 1;
    }
  `

const StyledIcon = styled(WalletIcon, {
  shouldForwardProp: (prop) => prop !== '$isDepositEmpty',
})<{ $isDepositEmpty: boolean }>(({ $isDepositEmpty, theme }) =>
  $isDepositEmpty
    ? css`
        animation: ${createBlinkAnimation(
            theme.rpTheme.colors.white,
            theme.rpTheme.colors.gold1
          )}
          1.4s ease-in-out infinite;
      `
    : undefined
)

const RefillText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$isDepositEmpty',
})<{ $isDepositEmpty: boolean }>(({ $isDepositEmpty, theme }) => [
  css`
    color: ${theme.rpTheme.colors.white};
    transition: color 0.2s;
  `,
  $isDepositEmpty &&
    css`
      animation: ${createBlinkAnimation(
          theme.rpTheme.colors.white,
          theme.rpTheme.colors.gold1
        )}
        1.4s ease-in-out infinite;
    `,
])

const Btn = styled.button<{ $isDepositEmpty: boolean }>`
  all: unset;
  opacity: 0.5;
  transition: opacity 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 1px;
  cursor: pointer;

  ${rpMediaQueries.tablet} {
    gap: 3px;
  }

  &:hover {
    opacity: 1;
  }

  ${({ $isDepositEmpty }) =>
    $isDepositEmpty &&
    css`
      opacity: 1;
    `}
  ${({ theme }) => css`
    &:hover ${StyledIcon}, &:hover ${RefillText} {
      animation: none;
      color: ${theme.rpTheme.colors.gold1};
    }
  `}
`

export const RefillDepositBtn: FC = observer(() => {
  const { uiStore, deposit } = useRussianPokerStore()
  const { t } = useTranslation()
  const isDepositEmpty = deposit === 0

  return (
    <Btn
      $isDepositEmpty={isDepositEmpty}
      data-cy="rp-refill-deposit-btn"
      onClick={() => {
        uiStore.partialUpdate({
          viewRefillPopup: true,
        })
      }}>
      <StyledIcon
        color="white"
        $isDepositEmpty={isDepositEmpty}
        size={{
          mobile: '12px',
          tablet: '20px',
          desktop: '20px',
        }}
      />
      <RefillText $isDepositEmpty={isDepositEmpty} variant="top" as="span">
        &nbsp;{t('refill')}
      </RefillText>
    </Btn>
  )
})
